<template>
  <DataTable
    v-model:expandedRows="expandedRows"
    :value="licenses"
    dataKey="id"
    @update:expanded-rows="onUpdateExpandedRows"
    @update:filters="onUpdateFilters"
    @rowExpand="handleRowExpand"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    paginator
    :rows="5"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    :filters="filters"
    filter-display="row"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    :globalFilterFields="globalFilterFields"
    :loading="loading"
  >
    <template #header>
      <div class="flex justify-content-end">
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="globalFilterValue" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column expander style="width: 5rem" />
    <Column
      field="globalStatus"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '3rem' }"
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
        ></Tag>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <Dropdown
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="statuses"
          placeholder="filtrar estado"
          class="p-column-filter flex align-items-center"
          style="min-width: 12rem; height: 3rem"
          :showClear="true"
        >
          <template #value="{ value }">
            <span class="text-sm text-600 font-medium">
              {{ value && formatRequestState(value) }}
            </span>
          </template>
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
            />
          </template>
        </Dropdown>
      </template>
    </Column>
    <Column header="Tipo de licencia" field="licenseType" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Observación" field="observation">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Cantidad" field="quantity" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Fecha de inicio" field="dateInit" sortable></Column>
    <Column header="Fecha final" field="dateEnd" sortable></Column>
    <Column header="Aprobadores">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column field="createdAt" header="Fecha de creacion"></Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <div class="actions">
          <ConfirmPopup></ConfirmPopup>
          <template v-if="data.files && data.files[0] !== ''">
            <Button
              v-for="file of data.files"
              :key="file"
              v-tooltip.bottom="'ver adjunto'"
              class="action-btn view hover:text-blue-800 mr-2"
              severity="secondary"
              icon="pi pi-paperclip"
              @click="handleDownload(file)"
            ></Button>
          </template>
          <Button
            v-if="
              data.approvedCount === 0 &&
              data.globalStatus === 'PENDING' &&
              data.origin === 'NATIVE'
            "
            v-tooltip.bottom="'Eliminar solicitud'"
            class="action-btn view btn hover:text-blue-800"
            severity="danger"
            icon="pi pi-trash"
            @click="handleDelete($event, data['id'])"
          ></Button>
        </div>
      </template>
    </Column>
    <template #expansion="slotProps">
      <div class="p-3">
        <h5 class="mb-1">Detalle de aprobadores</h5>
        <DataTable :value="slotProps.data.steps">
          <template
            #empty
            v-if="slotProps.data.origin === 'PAYROLLSYSTEM'"
            class="flex flex-justify-center"
          >
            Solicitud creada desde el sistema de nómina
          </template>
          <template
            #empty
            v-else-if="slotProps.data.origin === 'RRHH'"
            class="flex flex-justify-center"
          >
            Solicitud creada por el área de RRHH
          </template>
          <template #empty v-else class="flex flex-justify-center">
            <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
            cargando...
          </template>
          <Column field="approver.name" header="Nombre"></Column>
          <Column field="resolutionDate" header="Fecha de resolución">
            <template #body="{ data, field }">
              <span v-if="data[field]" v-local-date="data[field]"></span>
              <span class="text-600 font-medium" v-else>Sin definir</span>
            </template>
          </Column>
          <Column field="weight" header="Prioridad"></Column>
          <Column field="resolutionStatus" header="Estado">
            <template #body="slotProps">
              <Tag
                :value="formatRequestState(slotProps.data.resolutionStatus)"
                :severity="getStatusLabel(slotProps.data.resolutionStatus)"
              >
              </Tag>
            </template>
          </Column>
          <Column field="resolutionComment" header="Comentario"> </Column>
        </DataTable>
      </div>
    </template>
  </DataTable>
</template>
<script setup lang="ts">
  import { computed, toRefs } from 'vue'
  import DataTable, {
    DataTableExpandedRows,
    DataTableFilterMeta,
    DataTableFilterMetaData,
  } from 'primevue/datatable'
  import Column from 'primevue/column'
  import Tag from 'primevue/tag'
  import Dropdown from 'primevue/dropdown'
  import ConfirmPopup from 'primevue/confirmpopup'
  import Button from 'primevue/button'
  import InputText from 'primevue/inputtext'
  import License from '../models/License'

  const props = defineProps<{
    licenses?: License[]
    statuses: string[]
    filters: DataTableFilterMeta
    expandedRows: DataTableExpandedRows | any[]
    loading?: boolean
  }>()

  const emit = defineEmits([
    'update:filters',
    'update:expandedRows',
    'row-expand',
    'download-attachment',
    'delete-request',
  ])

  const onUpdateExpandedRows = (value: DataTableExpandedRows | any[]) => {
    emit('update:expandedRows', value)
  }

  const onUpdateFilters = (value: DataTableFilterMeta) => {
    emit('update:filters', value)
  }

  const { expandedRows, filters, licenses, statuses } = toRefs(props)

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      default:
        return 'Pendiente'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      default:
        return 'warning'
    }
  }

  const globalFilterFields = [
    'licenseType',
    'observation',
    'quantity',
    'dateInit',
    'dateEnd',
    'createdAt',
  ]

  const globalFilterValue = computed<string | null>({
    get: () => {
      const globalMeta = filters.value.global as
        | DataTableFilterMetaData
        | undefined
      return (globalMeta?.value as string | null | undefined) ?? null
    },
    set: (value) => {
      const currentFilters: DataTableFilterMeta = { ...filters.value }
      const globalMeta = currentFilters.global as
        | DataTableFilterMetaData
        | undefined
      currentFilters.global = {
        matchMode: globalMeta?.matchMode ?? 'contains',
        value,
      }
      emit('update:filters', currentFilters)
    },
  })

  const handleRowExpand = (event: any) => emit('row-expand', event)
  const handleDownload = (fileKey: string) =>
    emit('download-attachment', fileKey)
  const handleDelete = (event: any, id: string) =>
    emit('delete-request', event, id)
</script>
